import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, LogOut, Languages } from 'lucide-react';
import { CommandPalette, CommandGroup, CommandItem } from '@vnetwork/ui/primitives';
import { useTheme } from '@vnetwork/ui/provider';
import { useAuth } from '@/auth/AuthProvider';
import { setLanguage } from '@/lib/i18n';
import { NAV_LEAVES } from './nav';

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ⌘K command palette — jump to any workspace page (flattened from the sidebar
 * nav) plus quick commands (theme, language, sign out). CommandPalette binds
 * ⌘K / Ctrl+K itself, so it works even while closed.
 */
export function CommandMenu({ open, onOpenChange }: CommandMenuProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [theme, setTheme] = useTheme();
  const { logout } = useAuth();

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      placeholder={t('common.search')}
    >
      <CommandGroup heading={t('app.name')}>
        {NAV_LEAVES.map((item) => (
          <CommandItem
            key={item.id}
            icon={item.icon}
            keywords={[t(item.labelKey)]}
            onSelect={() => go(item.path!)}
          >
            {t(item.labelKey)}
          </CommandItem>
        ))}
      </CommandGroup>

      <CommandGroup heading="Commands">
        <CommandItem
          icon={theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          onSelect={() => {
            setTheme(theme === 'light' ? 'dark' : 'light');
            onOpenChange(false);
          }}
        >
          {theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        </CommandItem>
        <CommandItem
          icon={<Languages size={16} />}
          onSelect={() => {
            setLanguage(i18n.language === 'vi' ? 'en' : 'vi');
            onOpenChange(false);
          }}
        >
          {i18n.language === 'vi' ? 'English' : 'Tiếng Việt'}
        </CommandItem>
        <CommandItem icon={<LogOut size={16} />} onSelect={() => logout()}>
          {t('auth.signOut')}
        </CommandItem>
      </CommandGroup>
    </CommandPalette>
  );
}
